import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { refresh } from "@/data/session";
import { addNewUserEmail } from "@/data/users";
import { Flex } from "@/eworkitComponents";
import { EmailHandlingModal } from "@/views/Settings/UserSettings/UserAccount/UserName/EmailHandlingModal";
import { SingleEmailSection } from "@/views/Settings/UserSettings/UserAccount/UserName/SingleEmailSection";

import { IUserSectionProps } from "./types";

interface IEmailsSectionProps extends IUserSectionProps {}

export const EmailsSection: FC<IEmailsSectionProps> = ({ onUpdate, record }) => {
  const { t } = useTranslation("session");
  const [isAddModalOpen, setAddModalOpen] = useState(false);

  const refreshUserHandler = useCallback(async () => {
    const newUser = await refresh();
    if (onUpdate) {
      onUpdate(newUser);
    }
  }, []);

  const afterNewEmailAdded = useCallback(async () => {
    await refreshUserHandler();
    setAddModalOpen(false);
  }, []);

  const emailsWithMatchingConsultants = useMemo(() => {
    return record.emails.map(e => ({
      email: e,
      consultantProfiles: record.consultantProfiles.filter(cp => cp.email === e.email),
    }));
  }, [record]);

  const sortEmails = useCallback((a, b) => {
    if (a.email.primary) return -1;
    else if (b.email.primary) return 1;
    else if (a.email.confirmed && b.email.confirmed) return a.consultantProfiles.length - b.consultantProfiles.length;
    else if (!a.email.confirmed && b.email.confirmed) return 1;
    else if (!b.email.confirmed && a.email.confirmed) return -1;
    else return a.email.email - b.email.email;
  }, []);

  return (
    <Flex direction="column" innerBorder innerPadding="md">
      {emailsWithMatchingConsultants.sort(sortEmails).map((emailWithConsultantProfiles, i) => (
        <SingleEmailSection key={i} onUpdate={refreshUserHandler} {...emailWithConsultantProfiles} />
      ))}

      <Flex>
        <Button
          variant="secondary"
          onClick={() => {
            setAddModalOpen(true);
          }}>
          {t("USERS.SETTINGS.USER_ACCOUNT.EMAILS.ADD_EMAIL_BUTTON")}
        </Button>
      </Flex>

      <EmailHandlingModal
        variant="ADD_EMAIL"
        hasEmail={true}
        onSubmit={addNewUserEmail}
        afterSubmit={afterNewEmailAdded}
        onClose={() => {
          setAddModalOpen(false);
        }}
        open={isAddModalOpen}
        size="xxs"
      />
    </Flex>
  );
};
